import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { purchaseOrderHelpers } from '../services/purchaseOrderService';

function formatNu(amount: number): string {
  return `Nu. ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Fetches image URL and returns base64 data URL for jsPDF, or null on failure */
async function fetchImageAsBase64(url: string): Promise<string | null> {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export interface PurchaseOrderPdfOptions {
  /** Company logo URL (fetched and converted to base64 internally) */
  logoUrl?: string | null;
  /** Pre-fetched logo as base64 data URL (e.g. from /auth/me/company-logo) – avoids CORS */
  logoDataUrl?: string | null;
}

/** Accepts any PO-like object (from types or page-local interface) */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function downloadPurchaseOrderPdf(
  order: any,
  options?: PurchaseOrderPdfOptions
): Promise<void> {
  const doc = new jsPDF();

  let startY = 14;
  const logoDataUrl = options?.logoDataUrl;
  const logoUrl = options?.logoUrl;

  let imgData: string | null = logoDataUrl || null;
  if (!imgData && logoUrl) {
    imgData = await fetchImageAsBase64(logoUrl);
  }
  if (imgData) {
    const imgW = 40;
    const imgH = 20;
    doc.addImage(imgData, 'PNG', 14, 10, imgW, imgH);
    startY = 38;
  }

  // Header
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Purchase Order', 14, startY);
  startY += 8;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`PO Number: ${order.po_number || '—'}`, 14, startY);
  startY += 6;
  doc.text(`Date: ${order.order_date ? purchaseOrderHelpers.formatDate(order.order_date) : '—'}`, 14, startY);
  startY += 6;
  doc.text(`Status: ${purchaseOrderHelpers.displayStatus(order).text}`, 14, startY);
  startY += 12;

  // Vendor & Location
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Vendor:', 14, startY);
  doc.setFont('helvetica', 'normal');
  startY += 6;
  doc.text(order.vendor?.name ?? (order.vendor_id ? `Vendor #${order.vendor_id}` : '—'), 14, startY);
  startY += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Delivery Location:', 14, startY);
  doc.setFont('helvetica', 'normal');
  startY += 6;
  doc.text(
    order.location?.name ?? (order.location_id ? `Location #${order.location_id}` : '—'),
    14,
    startY
  );
  startY += 6;
  if (order.location?.address) {
    doc.setFontSize(9);
    doc.text(order.location.address, 14, startY);
    startY += 6;
  }
  startY += 4;

  // Expected delivery
  if (order.expected_delivery_date) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Expected Delivery:', 14, startY);
    doc.setFont('helvetica', 'normal');
    startY += 6;
    doc.text(purchaseOrderHelpers.formatDate(order.expected_delivery_date), 14, startY);
    startY += 6;
  }

  startY += 4;

  // Items table (support both items and order_items)
  const items = order.items || (order as any).order_items || [];
  const tableData = items.map((item: any) => [
    item.vendor_product?.name || item.product?.name || `Line #${item.vendor_product_id ?? '?'}`,
    String(item.quantity),
    formatNu(item.unit_price || 0),
    formatNu(item.total_price || 0),
    String(item.received_quantity ?? 0),
    String(Math.max(0, Number(item.quantity || 0) - Number(item.received_quantity || 0))),
  ]);

  autoTable(doc, {
    startY,
    head: [['Vendor product', 'Qty', 'Unit Price', 'Total', 'Received', 'Still to receive']],
    body: tableData,
    theme: 'grid',
    headStyles: { fillColor: [66, 139, 202], textColor: 255 },
    margin: { left: 14 },
  });

  const finalY = (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? startY + 40;

  // Total
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(
    `Total Amount: ${formatNu(order.total_amount || 0)}`,
    14,
    finalY + 16
  );

  if (order.notes) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const notesLines = doc.splitTextToSize(`Notes: ${order.notes}`, 180);
    doc.text(notesLines, 14, finalY + 26);
  }

  // Save
  const filename = `PO-${order.po_number || order.id}.pdf`;
  doc.save(filename);
}
